import { supabase } from "@/integrations/supabase/client";

export type DirectoryProfile = {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
  organization_name: string | null;
  avatar_url?: string | null;
  email?: string | null;
  phone?: string | null;
};

export function uniqueIds(values: Array<string | null | undefined>) {
  return Array.from(new Set(values.filter((value): value is string => Boolean(value))));
}

export async function fetchDirectoryProfileMap(ids: Array<string | null | undefined>) {
  const targetIds = uniqueIds(ids);
  if (targetIds.length === 0) {
    return new Map<string, DirectoryProfile>();
  }

  const { data, error } = await supabase.rpc("get_directory_profiles");
  if (error) {
    throw error;
  }

  const allowedIds = new Set(targetIds);
  return new Map<string, DirectoryProfile>(
    (data || [])
      .filter((profile) => allowedIds.has(profile.id))
      .map((profile) => [
        profile.id,
        {
          id: profile.id,
          first_name: profile.first_name,
          last_name: profile.last_name,
          role: profile.role,
          organization_name: profile.organization_name,
          avatar_url: profile.avatar_url,
          email: null,
          phone: null,
        },
      ]),
  );
}

export async function fetchMessageProfileMap(ids: Array<string | null | undefined>) {
  const targetIds = uniqueIds(ids);
  if (targetIds.length === 0) {
    return new Map<string, DirectoryProfile>();
  }

  const records = await Promise.all(
    targetIds.map(async (profileId) => {
      const { data, error } = await supabase.rpc("get_message_profile" as never, {
        p_profile_id: profileId,
      } as never);

      if (error) {
        throw error;
      }

      const profile = Array.isArray(data) ? data[0] : data;
      if (!profile?.id) {
        return null;
      }

      return [
        profile.id,
        {
          id: profile.id,
          first_name: profile.first_name,
          last_name: profile.last_name,
          role: profile.role,
          organization_name: profile.organization_name,
          avatar_url: profile.avatar_url,
          email: null,
          phone: null,
        },
      ] as const;
    }),
  );

  return new Map<string, DirectoryProfile>(
    records.filter((entry): entry is readonly [string, DirectoryProfile] => Boolean(entry)),
  );
}
